import { authService } from '../auth/auth.service';
import { getActiveRoles, hasAutoSubscribe } from '../auth/authHelpers';
import { serviceWorkerRegistration } from '../registerWorkers';
import storeService from '../store/store.service';
import endpoint from '../util/endpoint';
import { firebaseMessaging } from '../util/firebase';
import { calcTopics, withoutTopic } from './push.helpers';
import { pushStore, updatePushState } from './push.store';
import config from '@/config/config';
import type { ActivePushTopic, PushMessage, PushTopic } from '@/push/types';
import type { ServiceWorkerMessage } from '@/serviceWorker/serviceWorkerMessage';
import { deleteToken, getToken } from '@firebase/messaging';
import { RequestError } from '@whiz-cart/node-shared/endpoint/index';
import { errorCode, WhizCartError } from '@whiz-cart/node-shared/errorToString';
import Queue from '@whiz-cart/node-shared/queue';
import { service } from '@whiz-cart/node-shared/service/service';
import sleep from '@whiz-cart/node-shared/sleep';
import { urlService } from '@whiz-cart/ui-shared/url/url.service';

const pushRefreshInterval = 6 * 60 * 60_000;
const pushRefreshMinGap = 10 * 60_000;

const unsubscribeTopic = ({ topic, storeGuid }: ActivePushTopic, token?: string | null) => {
    const body = token ? { token } : undefined;
    return storeGuid === null
        ? endpoint('storeManager.unsubscribeGlobal', { topic }).delete(body)
        : endpoint('storeManager.unsubscribe', { storeGuid, topic }).delete(body);
};

export const pushService = service(
    'pushService',
    class PushService {
        queue = new Queue();
        isInitialized = false;

        reportError(error: WhizCartError, pushTopic?: ActivePushTopic, storeGuid?: string) {
            if (error.message) {
                console.error(error);
            }

            const topic = pushTopic?.topic.startsWith('sessionMonitor_') ? 'sessionMonitor' : pushTopic?.topic;

            const topics = pushStore
                .get()
                .topics.map((x) =>
                    !pushTopic || (x.topic === topic && (x.type === 'store' ? storeGuid : x.storeGuid) === pushTopic.storeGuid)
                        ? { ...x, error }
                        : x,
                );

            updatePushState({ topics });
        }

        resetError(pushTopic?: ActivePushTopic, storeGuid?: string) {
            const topics = pushStore
                .get()
                .topics.map((x) =>
                    !pushTopic || (x.topic === pushTopic.topic && (x.type === 'store' ? storeGuid : x.storeGuid) === pushTopic.storeGuid)
                        ? { ...x, error: undefined }
                        : x,
                );

            updatePushState({ topics });
        }

        async deleteToken() {
            console.debug('Hard reset push notifications');
            updatePushState({ token: null });

            try {
                const messaging = await firebaseMessaging();
                if (messaging) {
                    await deleteToken(messaging);
                }
                console.debug(`Deleted token on Firebase's end`);
            } catch (e) {
                console.error(`Failed to delete token on Firebase's end`, e);
            }
        }

        /** Delete and reestablish all push subscriptions */
        updateSubscriptions = () => {
            this.queue.clear(null);
            this.queue.schedule(async () => {
                try {
                    updatePushState({ inProgress: true });
                    const push = pushStore.get();
                    const currentStoreGuid = urlService.state.get().params?.storeGuid;
                    const loginState = authService.loginState.get();
                    if (!loginState.isLoggedIn) return;

                    // Tenant roles resolve through the store list; without it their topics would be unsubscribed
                    const stores = await storeService.allStores().get();
                    const roles = getActiveRoles(loginState, stores)('store', currentStoreGuid);
                    const topics = calcTopics(push.topics, [...roles], currentStoreGuid ?? null);

                    const { publicVapidKey } = config.firebase;
                    let topicsActive = [...(push.topicsActive ?? [])];
                    const obsoleteTopics = topicsActive.filter((a) => {
                        return !topics.some((b) => a.topic === b.topic && a.storeGuid === b.storeGuid);
                    });

                    await Promise.all(
                        obsoleteTopics.map(async (obsolete) => {
                            try {
                                await unsubscribeTopic(obsolete, push.token);
                                topicsActive = withoutTopic(topicsActive, obsolete);
                            } catch {
                                // ignore
                            }
                        }),
                    );

                    updatePushState({ topicsActive });

                    if (obsoleteTopics.length > 0) {
                        await this.deleteToken();
                    }

                    if (topics.length > 0) {
                        const messaging = await firebaseMessaging();

                        if (!messaging) {
                            return this.reportError(
                                new WhizCartError(undefined, {
                                    message: '',
                                    userMessage: 'Push Benachrichtigungen werden in diesem Browser nicht unterstützt',
                                }),
                            );
                        }

                        let token;

                        for (let tries = 0; ; tries++) {
                            try {
                                do {
                                    token = await getToken(messaging, {
                                        vapidKey: publicVapidKey,
                                        serviceWorkerRegistration: await serviceWorkerRegistration,
                                    });
                                } while (!token);
                                console.debug('Token:', token);
                                break;
                            } catch (e) {
                                if (errorCode(e) === 'messaging/permission-blocked') {
                                    return this.reportError(
                                        new WhizCartError(e, {
                                            message: 'Unable to get permission to notify.',
                                            userMessage: 'Berechtigung für PushService nicht erteilt',
                                        }),
                                    );
                                }

                                if (tries >= 10) {
                                    return this.reportError(
                                        new WhizCartError(e, {
                                            message: 'An error occurred while retrieving token.',
                                            userMessage: 'Einrichten des PushService fehlgeschlagen',
                                        }),
                                    );
                                }

                                console.warn('Failed to get token, retrying in 10s', e);
                                await sleep(10_000);
                            }
                        }

                        updatePushState({ token });
                        this.resetError();

                        await Promise.all(
                            topics.map(async ({ storeGuid, topic }) => {
                                try {
                                    if (storeGuid === null) {
                                        await endpoint('storeManager.pushSubscribeGlobal').post({ topic, token });
                                    } else {
                                        await endpoint('storeManager.pushSubscribe', { storeGuid }).post({ topic, token });
                                    }

                                    if (!topicsActive.some((a) => a.topic === topic && a.storeGuid === storeGuid)) {
                                        topicsActive.push({ topic, storeGuid });
                                    }
                                } catch (e) {
                                    if (e instanceof RequestError && e.response.status === 401) {
                                        return this.reportError(
                                            new WhizCartError(e, {
                                                message: `Missing authorization to subscribe to topic ${topic} for store ${storeGuid}`,
                                                userMessage: 'Ihnen fehlt die Berechtigung, diese Benachrichtigungen zu abonnieren',
                                            }),
                                        );
                                    }

                                    const message = e instanceof RequestError ? JSON.stringify(e.data) : undefined;

                                    this.reportError(
                                        new WhizCartError(e, {
                                            message: 'Failed to send notification to backend',
                                            userMessage:
                                                'Einrichten der Benachrichtigungen im Backend fehlgeschlagen' +
                                                (message ? `: ${message}` : ''),
                                        }),
                                        { topic, storeGuid },
                                        currentStoreGuid,
                                    );
                                }
                            }),
                        );
                    }

                    updatePushState({ topicsActive });
                    console.debug('Push subscriptions:', currentStoreGuid, topicsActive);
                } catch (e) {
                    this.reportError(new WhizCartError(e, { message: 'Unexpected error' }));
                } finally {
                    updatePushState({ inProgress: false });
                }
            });
        };

        /** Best effort and bounded, so logout never hangs on a dead network. */
        async unsubscribeAll() {
            const { topicsActive = [], token } = pushStore.get();

            await Promise.race([
                Promise.all(topicsActive.map((active) => unsubscribeTopic(active, token).catch(() => undefined))),
                sleep(5_000),
            ]);

            updatePushState({ topics: [], topicsActive: [] });
        }

        cleanupAfterLogout() {
            this.queue.schedule(async () => {
                updatePushState({ topicsActive: [] });
                await Promise.race([this.deleteToken(), sleep(5_000)]);
            });
        }

        async toggleTopic(pushTopic: PushTopic, force?: boolean) {
            let topics = pushStore.get().topics ?? [];
            const isActive = topics.some(
                (x) => x.type === pushTopic.type && x.topic === pushTopic.topic && x.storeGuid === pushTopic.storeGuid,
            );

            if (force === false || (force === undefined && isActive)) {
                topics = topics.filter(
                    (x) => !(x.type === pushTopic.type && x.topic === pushTopic.topic && x.storeGuid === pushTopic.storeGuid),
                );
            } else if (!isActive) {
                topics = [...topics, pushTopic];
            }

            updatePushState({ topics });
            this.updateSubscriptions();
        }

        async resetGlobalTopic(topic: string) {
            const topics = pushStore.get().topics.filter((x) => !(x.type === 'global' && x.topic === topic));
            updatePushState({ topics });
            this.updateSubscriptions();
        }

        async sendMessage(
            topic = 'sessionMonitor_service',
            payload: PushMessage = {
                id: crypto.randomUUID(),
                type: 'ticket',
                title: 'Cart 901 - SOS',
                body: `${new Date().toLocaleTimeString()} Preisabweichung`,
                tag: '1234567890',
                reNotify: true,
                redirectUrl: '/customer/901',
                data: {},
            },
        ) {
            const { storeGuid } = urlService.state.get().params;
            endpoint('storeManager.pushSend', { storeGuid: storeGuid ?? '', topic }).post(payload);
        }
    },
);

export const managePushSubscriptions = () => [
    pushStore
        .map((push) => push.permission)
        .subscribe(async () => {
            pushService.updateSubscriptions();
        }),

    urlService.state
        .map((url) => url.params?.storeGuid)
        .subscribe(async () => {
            pushService.updateSubscriptions();
        }),

    authService.loginState
        .map((loginState) => loginState.isLoggedIn)
        .subscribe(async (isLoggedIn, wasLoggedIn) => {
            if (!isLoggedIn && wasLoggedIn) {
                pushService.cleanupAfterLogout();
                return;
            }
            pushService.updateSubscriptions();
        }),

    (() => {
        let lastRefresh = Date.now();
        const refresh = () => {
            const { topicsActive } = pushStore.get();
            if (!authService.loginState.get().isLoggedIn || !topicsActive?.length) return;
            lastRefresh = Date.now();
            pushService.updateSubscriptions();
        };
        const onVisible = () => {
            if (document.visibilityState === 'visible' && Date.now() - lastRefresh > pushRefreshMinGap) refresh();
        };
        document.addEventListener('visibilitychange', onVisible);
        const interval = setInterval(refresh, pushRefreshInterval);
        return () => {
            document.removeEventListener('visibilitychange', onVisible);
            clearInterval(interval);
        };
    })(),

    authService.loginState.map(hasAutoSubscribe).subscribe((autoSubscribe) => {
        if (!autoSubscribe) return;
        const topics = pushStore.get().topics ?? [];
        if (topics.some((x) => x.type === 'store' && x.topic === 'sessionMonitor')) return;
        updatePushState({ topics: [...topics, { type: 'store', topic: 'sessionMonitor' }] });
    }),

    (() => {
        const messageListener = ({ data }: MessageEvent<ServiceWorkerMessage>) => {
            if (data.type !== 'navigate') {
                return;
            }

            console.debug('Received message from service worker to navigate to:', data.navigate);
            const { pathname, searchParams } = new URL(data.navigate, window.location.href);
            urlService.pushUrl(pathname, Object.fromEntries(searchParams));
        };
        navigator?.serviceWorker?.addEventListener('message', messageListener);
        return () => navigator?.serviceWorker?.removeEventListener('message', messageListener);
    })(),

    (() => {
        let cancel: () => void;

        (async () => {
            let stopped = false;
            cancel = () => (stopped = true);

            const navigatorPermissions = await navigator.permissions?.query({ name: 'notifications' });
            if (stopped) return;

            const updatePermission = async () => {
                const permission = navigatorPermissions?.state;

                updatePushState({
                    supported: !!window.navigator.serviceWorker,
                    permission,
                });
            };

            updatePermission();
            navigatorPermissions?.addEventListener('change', updatePermission);
            cancel = () => navigatorPermissions?.removeEventListener('change', updatePermission);
        })();

        return () => cancel?.();
    })(),
];
